// src/components/Festivales/FestivalesPanel.jsx
import { useState, useEffect, useCallback } from 'react';
import { downloadReceiptAsImage } from '../../utils/receiptToImage';

// ─── Helpers ────────────────────────────────────────────────────────────────
const mxn = (n) =>
  Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

const API = (path) => `/api/${path}`;

const statusColor = {
  pendiente: { bg: '#fff3cd', color: '#856404', label: 'Pendiente' },
  abonando:  { bg: '#cfe2ff', color: '#084298', label: 'Abonando'  },
  liquidado: { bg: '#d1e7dd', color: '#0f5132', label: 'Liquidado' },
};

// ─── Comprobante ─────────────────────────────────────────────────────────────
const buildReceiptHTML = (academyName, studentName, festival, payments, totalPaid) => {
  const rows = payments.map(p => `
    <tr>
      <td>${p.payment_date}</td>
      <td style="text-align:right">${mxn(p.amount)}</td>
      <td>${p.notes || '—'}</td>
    </tr>`).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
  <title>Comprobante Festival</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Inter',system-ui,sans-serif;background:#f3f4f6;display:flex;justify-content:center;padding:40px 16px}
    .card{background:white;width:100%;max-width:520px;border-radius:20px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.12)}
    .header{background:linear-gradient(135deg,#961914,#c0392b);padding:32px;color:white}
    .header-top{display:flex;justify-content:space-between;align-items:flex-start}
    .brand h1{font-size:20px;font-weight:700}.brand p{font-size:12px;opacity:.75;margin-top:3px}
    .body{padding:24px 32px}
    .info-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f3f4f6;font-size:13px}
    .info-row .lbl{color:#6b7280}.info-row .val{font-weight:600;color:#111827}
    .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:12px;font-weight:600;
           background:${totalPaid >= festival.total_amount ? '#d1e7dd' : '#cfe2ff'};
           color:${totalPaid >= festival.total_amount ? '#0f5132' : '#084298'}}
    table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px}
    th{background:#f5f5f5;padding:6px 8px;text-align:left;font-size:11px;color:#6b7280;text-transform:uppercase}
    td{padding:8px;border-bottom:1px solid #f0f0f0}
    .total-row td{font-weight:700;background:#f9f9f9}
    .footer{padding:16px 32px;background:#fafafa;border-top:1px solid #f3f4f6;font-size:11px;color:#9ca3af;text-align:center}
  </style></head><body>
  <div class="card">
    <div class="header">
      <div class="header-top">
        <div class="brand"><h1>${academyName}</h1><p>Comprobante de abono — Festival</p></div>
      </div>
    </div>
    <div class="body">
      <div class="info-row"><span class="lbl">Alumna</span><span class="val">${studentName}</span></div>
      <div class="info-row"><span class="lbl">Festival</span><span class="val">${festival.name}</span></div>
      <div class="info-row"><span class="lbl">Monto total</span><span class="val">${mxn(festival.total_amount)}</span></div>
      <div class="info-row"><span class="lbl">Total abonado</span><span class="val">${mxn(totalPaid)}</span></div>
      <div class="info-row"><span class="lbl">Saldo pendiente</span><span class="val">${mxn(Math.max(0, festival.total_amount - totalPaid))}</span></div>
      <div class="info-row"><span class="lbl">Estado</span><span class="val"><span class="badge">${totalPaid >= festival.total_amount ? 'Liquidado' : 'Abonando'}</span></span></div>
      <table>
        <thead><tr><th>Fecha</th><th>Monto</th><th>Notas</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="total-row"><td>Total</td><td style="text-align:right">${mxn(totalPaid)}</td><td></td></tr></tfoot>
      </table>
    </div>
    <div class="footer">Generado el ${new Date().toLocaleDateString('es-MX')}</div>
  </div>
  </body></html>`;
};

// ─── Modal: Crear / Editar Festival ──────────────────────────────────────────
const FestivalModal = ({ festival, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: festival?.name || '', event_date: festival?.event_date || '', total_amount: festival?.total_amount || '' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSave = async () => {
    if (!form.name || !form.total_amount) { setError('Nombre y monto son requeridos'); return; }
    setSaving(true);
    try {
      const res  = await fetch(API('save_festival.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, id: festival?.id }) });
      const data = await res.json();
      if (data.success) onSaved(); else setError(data.error || 'Error al guardar');
    } catch { setError('Error de red'); }
    setSaving(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={{ margin: '0 0 16px' }}>{festival ? 'Editar festival' : 'Nuevo festival'}</h3>
        {error && <div style={styles.errorBox}>{error}</div>}
        <label style={styles.label}>Nombre</label>
        <input style={styles.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
        <label style={styles.label}>Fecha del evento</label>
        <input style={styles.input} type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} />
        <label style={styles.label}>Monto total por alumna (MXN)</label>
        <input style={styles.input} type="number" min="0" value={form.total_amount} onChange={e => setForm(f => ({ ...f, total_amount: e.target.value }))} />
        <div style={styles.modalActions}>
          <button style={styles.btnSecondary} onClick={onClose}>Cancelar</button>
          <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Inscribir alumnas ─────────────────────────────────────────────────
const EnrollModal = ({ festivalId, onClose, onSaved }) => {
  const [allStudents, setAllStudents] = useState([]);
  const [enrolled,    setEnrolled]    = useState([]);
  const [selected,    setSelected]    = useState([]);
  const [search,      setSearch]      = useState('');
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(API('list_students.php')).then(r => r.json()),
      fetch(API(`list_festival_students.php?festival_id=${festivalId}`)).then(r => r.json()),
    ]).then(([students, fs]) => { setAllStudents(students); setEnrolled(fs.map(s => s.student_id)); });
  }, [festivalId]);

  const available = allStudents.filter(s => !enrolled.includes(s.id) && s.name.toLowerCase().includes(search.toLowerCase()));
  const toggle    = id => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleEnroll = async () => {
    if (!selected.length) return;
    setSaving(true);
    const res  = await fetch(API('enroll_festival_student.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ festival_id: festivalId, student_ids: selected }) });
    const data = await res.json();
    if (data.success) onSaved();
    setSaving(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: 420 }}>
        <h3 style={{ margin: '0 0 12px' }}>Inscribir alumnas</h3>
        <input style={styles.input} placeholder="Buscar alumna…" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: 16 }}>
          {available.length === 0
            ? <div style={{ padding: 16, color: '#888', fontSize: 14 }}>No hay alumnas disponibles</div>
            : available.map(s => (
              <div key={s.id} onClick={() => toggle(s.id)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', background: selected.includes(s.id) ? '#e8f4ff' : 'white', display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" readOnly checked={selected.includes(s.id)} />
                <span style={{ fontSize: 14 }}>{s.name}</span>
              </div>
            ))}
        </div>
        <div style={styles.modalActions}>
          <button style={styles.btnSecondary} onClick={onClose}>Cancelar</button>
          <button style={styles.btnPrimary} onClick={handleEnroll} disabled={saving || !selected.length}>{saving ? 'Inscribiendo…' : `Inscribir (${selected.length})`}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Registrar abono ───────────────────────────────────────────────────
const AbonoModal = ({ festivalStudent, onClose, onSaved }) => {
  const remaining     = festivalStudent.total_amount - festivalStudent.amount_paid;
  const [form, setForm]     = useState({ amount: '', payment_date: new Date().toISOString().slice(0, 10), notes: '' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSave = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Ingresa un monto válido'); return; }
    if (amt > remaining + 0.01) { setError(`El monto excede el saldo (${mxn(remaining)})`); return; }
    setSaving(true);
    try {
      const res  = await fetch(API('register_festival_payment.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ festival_student_id: festivalStudent.id, ...form }) });
      const data = await res.json();
      if (data.success) onSaved(data); else setError(data.error || 'Error al registrar');
    } catch { setError('Error de red'); }
    setSaving(false);
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <h3 style={{ margin: '0 0 4px' }}>Registrar abono</h3>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>{festivalStudent.student_name} · Saldo pendiente: <strong>{mxn(remaining)}</strong></p>
        {error && <div style={styles.errorBox}>{error}</div>}
        <label style={styles.label}>Monto (MXN)</label>
        <input style={styles.input} type="number" min="1" max={remaining} value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
        <label style={styles.label}>Fecha</label>
        <input style={styles.input} type="date" value={form.payment_date} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
        <label style={styles.label}>Notas (opcional)</label>
        <input style={styles.input} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Ej: pago en efectivo" />
        <div style={styles.modalActions}>
          <button style={styles.btnSecondary} onClick={onClose}>Cancelar</button>
          <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? 'Guardando…' : 'Registrar abono'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Historial de abonos ───────────────────────────────────────────────
const HistorialModal = ({ festivalStudent, festival, academyName, onClose, onDeleted }) => {
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res  = await fetch(API(`list_festival_payments.php?festival_student_id=${festivalStudent.id}`));
    const data = await res.json();
    setPayments(data);
    setLoading(false);
  }, [festivalStudent.id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este abono?')) return;
    await fetch(API('delete_festival_payment.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
    onDeleted();
    load();
  };

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount), 0);

  // FIX: descarga PNG + abre WhatsApp si hay teléfono
  const handleComprobante = () => {
    const html = buildReceiptHTML(academyName, festivalStudent.student_name, festival, payments, totalPaid);
    const filename = `Festival-${festivalStudent.student_name.replace(/\s+/g, '-')}`;
    const phone    = festivalStudent.student_phone || null;
    downloadReceiptAsImage(html, filename, phone ? { phone, studentName: festivalStudent.student_name, type: 'festival' } : null);
  };

  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>Historial de abonos</h3>
          <button style={styles.btnPrimary} onClick={handleComprobante}>📸 Comprobante</button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>{festivalStudent.student_name} · {mxn(totalPaid)} / {mxn(festivalStudent.total_amount)}</p>
        {loading
          ? <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Cargando…</div>
          : payments.length === 0
            ? <div style={{ padding: 16, color: '#888', fontSize: 14 }}>Sin abonos registrados</div>
            : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead><tr style={{ background: '#f5f5f5' }}><th style={styles.th}>Fecha</th><th style={styles.th}>Monto</th><th style={styles.th}>Notas</th><th style={styles.th}></th></tr></thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={styles.td}>{p.payment_date}</td>
                      <td style={styles.td}>{mxn(p.amount)}</td>
                      <td style={styles.td}>{p.notes || '—'}</td>
                      <td style={styles.td}><button style={styles.btnDanger} onClick={() => handleDelete(p.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
        }
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button style={styles.btnSecondary} onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
};

// ─── Vista: Detalle de festival ───────────────────────────────────────────────
const FestivalDetail = ({ festival, academyName, onBack }) => {
  const [students,     setStudents]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [showEnroll,   setShowEnroll]   = useState(false);
  const [abonoFor,     setAbonoFor]     = useState(null);
  const [historialFor, setHistorialFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res  = await fetch(API(`list_festival_students.php?festival_id=${festival.id}`));
    const data = await res.json();
    setStudents(data);
    setLoading(false);
  }, [festival.id]);

  useEffect(() => { load(); }, [load]);

  const handleUnenroll = async fs => {
    if (!window.confirm(`¿Desinscribir a ${fs.student_name}?`)) return;
    const res  = await fetch(API('unenroll_festival_student.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ festival_student_id: fs.id }) });
    const data = await res.json();
    if (!data.success) alert(data.error);
    load();
  };

  const filtered       = students.filter(s => s.student_name.toLowerCase().includes(search.toLowerCase()));
  const totalCollected = students.reduce((s, r) => s + parseFloat(r.amount_paid), 0);
  const totalExpected  = students.reduce((s, r) => s + parseFloat(r.total_amount), 0);
  const pct            = totalExpected > 0 ? Math.round((totalCollected / totalExpected) * 100) : 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <button style={styles.btnSecondary} onClick={onBack}>← Festivales</button>
        <h2 style={{ margin: 0, flex: 1 }}>{festival.name}</h2>
        <button style={styles.btnPrimary} onClick={() => setShowEnroll(true)}>+ Inscribir alumnas</button>
      </div>
      {festival.event_date && <p style={{ margin: '0 0 16px', fontSize: 13, color: '#888' }}>📅 {festival.event_date} · {mxn(festival.total_amount)} por alumna</p>}
      <div style={{ background: '#f0f0f0', borderRadius: 8, marginBottom: 20, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span>Recaudado: <strong>{mxn(totalCollected)}</strong></span>
          <span>Meta: <strong>{mxn(totalExpected)}</strong></span>
          <span><strong>{pct}%</strong></span>
        </div>
        <div style={{ background: '#ddd', borderRadius: 4, height: 8 }}>
          <div style={{ width: `${pct}%`, background: '#0d6efd', borderRadius: 4, height: 8, transition: 'width .3s' }} />
        </div>
      </div>
      <input style={{ ...styles.input, marginBottom: 12 }} placeholder="Buscar alumna…" value={search} onChange={e => setSearch(e.target.value)} />
      {loading
        ? <div style={{ padding: 32, textAlign: 'center', color: '#888' }}>Cargando…</div>
        : filtered.length === 0
          ? <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>{students.length === 0 ? 'No hay alumnas inscritas aún.' : 'Sin resultados.'}</div>
          : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead><tr style={{ background: '#f5f5f5' }}><th style={styles.th}>Alumna</th><th style={styles.th}>Total</th><th style={styles.th}>Abonado</th><th style={styles.th}>Saldo</th><th style={styles.th}>Estado</th><th style={styles.th}>Acciones</th></tr></thead>
              <tbody>
                {filtered.map(fs => {
                  const saldo = parseFloat(fs.total_amount) - parseFloat(fs.amount_paid);
                  const sc    = statusColor[fs.status];
                  return (
                    <tr key={fs.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={styles.td}>{fs.student_name}</td>
                      <td style={styles.td}>{mxn(fs.total_amount)}</td>
                      <td style={styles.td}>{mxn(fs.amount_paid)}</td>
                      <td style={styles.td}>{mxn(Math.max(0, saldo))}</td>
                      <td style={styles.td}><span style={{ background: sc.bg, color: sc.color, padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{sc.label}</span></td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {fs.status !== 'liquidado' && <button style={styles.btnSmallPrimary} onClick={() => setAbonoFor(fs)}>+ Abono</button>}
                          <button style={styles.btnSmallSecondary} onClick={() => setHistorialFor(fs)}>Historial</button>
                          {fs.status === 'pendiente' && <button style={styles.btnDanger} onClick={() => handleUnenroll(fs)}>✕</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
      }
      {showEnroll   && <EnrollModal festivalId={festival.id} onClose={() => setShowEnroll(false)} onSaved={() => { setShowEnroll(false); load(); }} />}
      {abonoFor     && <AbonoModal festivalStudent={abonoFor} onClose={() => setAbonoFor(null)} onSaved={() => { setAbonoFor(null); load(); }} />}
      {historialFor && <HistorialModal festivalStudent={historialFor} festival={festival} academyName={academyName} onClose={() => setHistorialFor(null)} onDeleted={() => load()} />}
    </div>
  );
};

// ─── Vista: Lista de festivales ───────────────────────────────────────────────
const FestivalesList = ({ onSelect }) => {
  const [festivals,  setFestivals]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modalData,  setModalData]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res  = await fetch(API('list_festivals.php'));
    const data = await res.json();
    setFestivals(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async f => {
    if (!window.confirm(`¿Eliminar "${f.name}"? Se borrarán todos sus registros.`)) return;
    await fetch(API('delete_festival.php'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: f.id }) });
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Festivales</h2>
        <button style={styles.btnPrimary} onClick={() => setModalData({})}>+ Nuevo festival</button>
      </div>
      {loading
        ? <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Cargando…</div>
        : festivals.length === 0
          ? <div style={{ padding: 40, textAlign: 'center', color: '#aaa' }}>No hay festivales. Crea el primero.</div>
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
              {festivals.map(f => {
                const pct = f.total_expected > 0 ? Math.round((f.total_collected / f.total_expected) * 100) : 0;
                return (
                  <div key={f.id} style={styles.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <strong style={{ fontSize: 16 }}>{f.name}</strong>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 16 }} onClick={() => setModalData(f)}>✏️</button>
                    </div>
                    {f.event_date && <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>📅 {f.event_date}</div>}
                    <div style={{ fontSize: 13, marginBottom: 4 }}>{f.total_students} alumnas · {mxn(f.total_amount)} c/u</div>
                    <div style={{ fontSize: 13, marginBottom: 8 }}>Recaudado: <strong>{mxn(f.total_collected)}</strong> / {mxn(f.total_expected)} ({pct}%)</div>
                    <div style={{ background: '#ddd', borderRadius: 4, height: 6, marginBottom: 14 }}>
                      <div style={{ width: `${pct}%`, background: '#0d6efd', borderRadius: 4, height: 6, transition: 'width .3s' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={() => onSelect(f)}>Ver detalle</button>
                      <button style={styles.btnDanger} onClick={() => handleDelete(f)}>🗑️</button>
                    </div>
                  </div>
                );
              })}
            </div>
      }
      {modalData !== null && <FestivalModal festival={modalData?.id ? modalData : null} onClose={() => setModalData(null)} onSaved={() => { setModalData(null); load(); }} />}
    </div>
  );
};

// ─── Componente raíz ──────────────────────────────────────────────────────────
const FestivalesPanel = ({ currentUser }) => {
  const [selected, setSelected] = useState(null);
  const academyName = currentUser?.academy_name || 'Academia';

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {selected
        ? <FestivalDetail festival={selected} academyName={academyName} onBack={() => setSelected(null)} />
        : <FestivalesList onSelect={setSelected} />
      }
    </div>
  );
};

export default FestivalesPanel;

// ─── Estilos ──────────────────────────────────────────────────────────────────
const styles = {
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal:           { background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,.15)', maxHeight: '90vh', overflowY: 'auto' },
  card:            { background: 'white', border: '1px solid #e0e0e0', borderRadius: 12, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  label:           { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#444' },
  input:           { width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 },
  errorBox:        { background: '#fff0f0', border: '1px solid #ffcccc', color: '#c00', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  modalActions:    { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  btnPrimary:      { background: '#0d6efd', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnSecondary:    { background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnDanger:       { background: '#fff0f0', color: '#c00', border: '1px solid #fcc', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 13 },
  btnSmallPrimary: { background: '#0d6efd', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnSmallSecondary:{ background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  th:              { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 13 },
  td:              { padding: '10px 12px' },
};
